import { useCallback } from 'react';
import styled from 'styled-components';

import { PrimaryButton, BUTTON_SIZES } from 'ui/button';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES } from 'ui/modal';
import { fontWeights } from 'ui/theme';
import { useToastNotification } from 'utils/hooks/useToastNotification';

import { useUserKey, useUserDisplayName } from '../../hooks';
import {
  ModalState,
  SubscribeToChannelsModalState,
  useModalsContext,
  useQueryContext,
} from '../contexts';
import {
  useChannelOrChannels,
  useFilteredModalState,
  useSubscribeUserToChannelMutation,
} from '../hooks';

const displayText = {
  title: 'Subscribe to channel',
  bulkTitle: 'Subscribe to channels',
  confirm: 'Subscribe',
  loading: 'Subscribing...',
};

const Bold = styled.b`
  font-weight: ${fontWeights.bold};
`;

const isSubscribingToChannels = (
  modalState?: ModalState | null,
): modalState is SubscribeToChannelsModalState =>
  ((modalState as SubscribeToChannelsModalState | undefined)?.subscribingToChannels?.length ?? 0) >
  0;

const getChannels = (state: SubscribeToChannelsModalState) => state.subscribingToChannels;

export const SubscribeToChannelsModal = () => {
  const userKey = useUserKey(true);
  const { hideModal } = useModalsContext();
  const subscribeChannels = useFilteredModalState(isSubscribingToChannels, getChannels);
  const { marketingChannels } = useQueryContext();
  const userDisplayName = useUserDisplayName();

  const {
    isSuccess,
    isLoading,
    isError,
    mutate: subscribe,
    reset,
  } = useSubscribeUserToChannelMutation();

  const channelOrChannels = useChannelOrChannels(subscribeChannels, marketingChannels);

  const onDone = useCallback(() => {
    hideModal();
    reset();
  }, [hideModal, reset]);

  useToastNotification({
    enabled: subscribeChannels.length > 0 && (isSuccess || isError),
    onDone,
    positive: isSuccess,
    negative: isError,
    positiveMessage: (
      <>
        Subscribed <Bold>{userDisplayName}</Bold> to <Bold>{channelOrChannels}</Bold>
      </>
    ),
    negativeMessage: (
      <>
        Could not subscribe <Bold>{userDisplayName}</Bold> to <Bold>{channelOrChannels}</Bold>
      </>
    ),
  });

  if (subscribeChannels.length === 0) {
    return null;
  }

  const handleSubmit = () => {
    subscribe({
      userKey,
      channelIds: subscribeChannels.map((c) => c.id),
    });
  };

  return (
    <Modal id="SubscribeToChannelsModal" isModalOpen onClose={hideModal} size={MODAL_SIZES.SMALL}>
      <ModalHeader>
        {subscribeChannels.length === 1 ? displayText.title : displayText.bulkTitle}
      </ModalHeader>
      <ModalBody>
        Are you sure that you want to subscribe <Bold>{userDisplayName}</Bold> to{' '}
        <Bold>{channelOrChannels}</Bold>?
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={handleSubmit}
          data-test="confirm-subscribe-button"
          isDisabled={isLoading}
          size={BUTTON_SIZES.LARGE}
        >
          {isLoading ? displayText.loading : displayText.confirm}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
